/*
 * File: Api.js
 * Project: mp-mobile-app
 *
 * Created by Sam Hookstra on 12/31/2019
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles API calls for patients/1/patient
 */
import { Http } from "api/Http";
import { Instance, types } from "mobx-state-tree";

const Api = new Http("patients/1/patient");

/**
 * A result returned from the patient search API.
 */
export const PatientSearchModel = types
  .model("PatientSearchModel", {
    firstName: types.string,
    middleName: types.maybe(types.string),
    lastName: types.string,
    birthday: types.maybe(types.string),
    age: types.maybe(types.string),
    photoHash: types.maybe(types.string),
    patientId: types.number,
    displayId: types.maybe(types.string),
    patientAlerts: types.boolean,
    accountAlerts: types.boolean,
    allergies: types.boolean,
    patientProviderId: types.maybe(types.number),
    accountProviderId: types.number
  })
  .views(self => ({
    /** Builds the patient's name from their first, middle, and last names. */
    patientName() {
      const names = [];
      if (self.firstName.length > 0) {
        names.push(self.firstName);
      }

      if (self.middleName != null && self.middleName.length > 0) {
        names.push(self.middleName);
      }

      if (self.lastName.length > 0) {
        names.push(self.lastName);
      }

      return names.join(" ");
    }
  }));

class PatientSearchApi {
  api: Http;
  constructor() {
    this.api = Api;
  }

  /**
   * Makes a request to search for patients.
   * @param {string | number[]} keyword A search string, or an array of patient ids to load.
   * @returns
   */
  search = async (
    keyword: string | number[]
  ): Promise<Instance<typeof PatientSearchModel>[]> => {
    let url;
    if (Array.isArray(keyword)) {
      url = "/?ids=" + encodeURIComponent(keyword.join(","));
    } else {
      url = "/?search=" + encodeURIComponent(keyword.trim());
    }

    const res = await this.api.get(url);
    return res.data.map((p: any) => PatientSearchModel.create(p)) || [];
  };
}

const newApi = new PatientSearchApi();
export default newApi;
